import type { HttpClient } from '@microsoft/signalr';
import { HumanCallNotifications } from './direct-call-notifications';
import type { HumanConnectionProofApi } from './direct-call-binding';
import type { HumanCallRequest } from './direct-call-http';

/** US 36579: the call-invite routes of the bridge, called with the HUMAN token. */
export interface VoipRegistration {
  deviceId: string;
  token: string;
  environment: 'production' | 'development';
  bundleId: string;
}

export interface RingListener {
  /** Raw `incomingCall` payload; the caller validates it. */
  incoming(payload: unknown): void;
  /** Raw `callInviteEnded` payload. */
  ended(payload: unknown): void;
  /** The hub could not connect, bind or stayed closed. */
  failed(): void;
}

export interface HumanRinger {
  registerVoip(body: VoipRegistration): Promise<number>;
  removeVoip(deviceId: string): Promise<number>;
  answer(inviteId: string, deviceId: string): Promise<number>;
  decline(inviteId: string): Promise<number>;
  /** Opens a private human-bound hub; returns the close function. */
  listen(listener: RingListener): () => void;
}

const DEADLINE_MS = 15000;

/** Status code only: server error bodies and tokens never reach the UI. */
export function createHumanRinger(
  options: {
    bridgeApi: string;
    humanToken: (signal: AbortSignal) => Promise<string>;
    request: HumanCallRequest;
    hubHttpClient?: HttpClient;
    webSocket?: typeof WebSocket;
  },
  proofApi: HumanConnectionProofApi
): HumanRinger {
  const url = new URL(options.bridgeApi);
  if (url.protocol !== 'https:' || url.username || url.password)
    throw new Error('An HTTPS bridge API URL is required');
  const base = url.href.replace(/\/$/, '');
  const send = async (
    path: string,
    method: string,
    body?: unknown
  ): Promise<number> => {
    const operation = new AbortController();
    const timer = setTimeout(() => operation.abort(), DEADLINE_MS);
    try {
      const token = await options.humanToken(operation.signal);
      if (!token?.trim()) throw new Error('Microsoft sign-in is required');
      const response = await options.request(base + path, {
        method,
        signal: operation.signal,
        redirect: 'error',
        credentials: 'omit',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      return response.status;
    } finally {
      clearTimeout(timer);
    }
  };
  const invite = (id: string, action: string) => {
    if (!/^[A-Za-z0-9-]{1,64}$/.test(id)) throw new Error('Invalid invite id');
    return `/call-invites/${id}/${action}`;
  };
  return {
    registerVoip: (body) => send('/devices/voip', 'POST', body),
    removeVoip: (deviceId) =>
      send(`/devices/voip/${encodeURIComponent(deviceId)}`, 'DELETE'),
    answer: (id, deviceId) => send(invite(id, 'answer'), 'POST', { deviceId }),
    decline: (id) => send(invite(id, 'decline'), 'POST', {}),
    listen(listener) {
      const hub = new HumanCallNotifications(
        options.bridgeApi,
        options.humanToken,
        proofApi,
        options.hubHttpClient,
        options.webSocket
      );
      hub.on('incomingCall', (value) => listener.incoming(value));
      hub.on('callInviteEnded', (value) => listener.ended(value));
      // subscribe() connects and proves the human binding the group join needs.
      return hub.subscribe(
        () => undefined,
        () => undefined,
        () => listener.failed()
      );
    },
  };
}
